if (!customElements.get('dynamic-comparison')) {
  customElements.define(
    'dynamic-comparison',
    class DynamicComparison extends HTMLElement {
      constructor() {
        super();
        this.slots = [];
        this.allProducts = [];
        this._baseProducts = [];
        this._currentCollectionProducts = [];
        this._productsLoaded = false;
        this.configRows = [];
        this._specRowsFromAttr = null;
        this._boundClickOutside = this._handleClickOutside.bind(this);
        this._modelNameRequests = new Set();
        this._tagGroups = [];
        this._activeDropdownIndex = new Map();
      }

      connectedCallback() {
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this._init());
        } else {
          this._init();
        }
      }

      disconnectedCallback() {
        document.removeEventListener('click', this._boundClickOutside);
        window.removeEventListener('scroll', this._boundRepositionDropdowns, true);
        window.removeEventListener('resize', this._boundRepositionDropdowns);
        this._detachedDropdowns = this._detachedDropdowns || new Map();
        this._detachedDropdowns.forEach((dd) => dd.remove());
      }

      _init() {
        this.MAX_SLOTS = parseInt(this.dataset.columns) || 3;
        this.slots = new Array(this.MAX_SLOTS).fill(null);
        this._showLearnMore = this.dataset.showLearnMore === 'true';
        this._learnMoreText = this.dataset.learnMoreText || 'Learn more';
        this._pricePrefix = this.dataset.pricePrefix || 'Starting at';
        this._showColorSwatches = this.dataset.showColorSwatches === 'true';
        this._tableTextTransform = (this.dataset.tableTextTransform || 'none').toLowerCase();
        this._showImage = this.dataset.showImage !== 'false';
        this._imageMetafield = (this.dataset.imageMetafield || '').trim();
        this._showModelNameDropdown = this.dataset.showModelNameDropdown === 'true';
        this._dropdownSubtitleMode = this.dataset.dropdownSubtitleMode || 'none';
        if (!this.dataset.dropdownSubtitleMode && this.dataset.showShortDescDropdown === 'true') {
          this._dropdownSubtitleMode = 'short_desc';
        }
        this._showShortDescDropdown = this._dropdownSubtitleMode === 'short_desc';
        this._showProductTitleSubtitle = this._dropdownSubtitleMode === 'product_title';
        this._useCurrentCollection = this.dataset.useCurrentCollection === 'true';
        this._tagGroups = this._parseTagGroups(this.dataset.tagGroups || '');

        // ── Detached dropdowns (escape overflow clip) ──
        this._detachedDropdowns = new Map();
        this.querySelectorAll('.dc-combobox[data-slot]').forEach((wrap) => {
          const slotIndex = parseInt(wrap.dataset.slot, 10);
          if (slotIndex >= this.MAX_SLOTS) return;
          const inlineList = wrap.querySelector('.dc-combobox__list');
          if (!inlineList) return;

          const dd = inlineList;
          dd.classList.add('dc-combobox__dropdown--detached');
          document.body.appendChild(dd);
          this._detachedDropdowns.set(slotIndex, dd);

          const input = wrap.querySelector('.dc-combobox__input');
          input.addEventListener('input', () => {
            this._filterDropdown(input.value.trim(), slotIndex);
            this._openDropdown(slotIndex);
          });
          input.addEventListener('focus', () => {
            input.value = '';
            this._openDropdown(slotIndex);
          });
          input.addEventListener('click', () => {
            input.value = '';
            this._openDropdown(slotIndex);
          });
          input.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
              this._closeDropdown(slotIndex);
              return;
            }

            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
              e.preventDefault();
              this._openDropdown(slotIndex);
              this._moveActiveDropdownOption(slotIndex, e.key === 'ArrowDown' ? 1 : -1);
              return;
            }

            if (e.key === 'Enter') {
              e.preventDefault();
              this._openDropdown(slotIndex);
              this._chooseActiveDropdownOption(slotIndex);
            }
          });
        });

        document.addEventListener('click', this._boundClickOutside);
        this._boundRepositionDropdowns = () => this._repositionOpenDropdowns();
        window.addEventListener('scroll', this._boundRepositionDropdowns, true);
        window.addEventListener('resize', this._boundRepositionDropdowns);

        var collectionOnlyToggle = this.querySelector('.dc-collection-only-toggle');
        if (collectionOnlyToggle) {
          collectionOnlyToggle.checked = !!this._useCurrentCollection;
          collectionOnlyToggle.addEventListener('change', () => {
            this._useCurrentCollection = collectionOnlyToggle.checked;
            this._rebuildProductList();
            this._closeAllDropdowns();
          });
        }

        // ── Mouse-drag scroll for mobile viewport ──
        this._initDragScroll();

        // ── Parse spec rows from Liquid attribute ──
        const specRowsAttr = this.dataset.specRows;
        if (specRowsAttr) {
          try {
            const parsed = JSON.parse(specRowsAttr);
            if (Array.isArray(parsed) && parsed.length > 0) {
              this._specRowsFromAttr = parsed.filter(r => r.label && r.label.trim());
            }
          } catch (e) {
            console.warn('[dynamic-comparison] Failed to parse data-spec-rows:', e);
          }
        }

        if (this._specRowsFromAttr) {
          this._specRowsFromAttr = this._specRowsFromAttr.map(r => ({
            key: (r.key && r.key.trim()) ? r.key.trim() : r.label.trim(),
            label: r.label.trim(),
            mode: r.mode || 'text',
            checkImg: r.checkImg || '',
            crossImg: r.crossImg || ''
          }));
        }

        // ── Load products ──
        const currentProductAttr = this.dataset.currentProduct;
        const loadCurrentProduct = currentProductAttr
          ? this._loadCurrentProduct(currentProductAttr)
          : Promise.resolve();

        Promise.all([this._loadAllProducts(), loadCurrentProduct]).then(() => {
          if (this._specRowsFromAttr && this._specRowsFromAttr.length > 0) {
            this.configRows = this._specRowsFromAttr;
          }

          this._renderSpecStructure();

          if (this.slots[0]) this.updateSlot(0);

          var currentHandle = this.slots[0] ? this.slots[0].handle : null;
          var startSlot = this.slots[0] ? 1 : 0;
          var needed = this.MAX_SLOTS - startSlot;
          var initialProducts = this._buildInitialFillProducts(currentHandle, needed);

          initialProducts.reduce((promise, product, idx) => {
            return promise.then(() => this._loadProductIntoSlot(product.handle, startSlot + idx));
          }, Promise.resolve());
        });

        // ── Metafield hint in Theme Editor ──
        if (window.Shopify && window.Shopify.designMode) {
          const availableKeys = this.dataset.availableMetafields;
          if (availableKeys && availableKeys.trim()) {
            const existing = this.querySelector('.dc-metafield-hint');
            if (existing) existing.remove();
            const hint = document.createElement('div');
            hint.className = 'dc-metafield-hint';
            hint.style.cssText = 'position:fixed;top:60px;right:20px;z-index:99999;background:#fff3cd;border:1px solid #ffc107;border-radius:4px;padding:12px;max-width:300px;box-shadow:0 4px 12px rgba(0,0,0,0.15);font-size:13px;color:#856404;';
            hint.innerHTML = '<strong style="display:block;margin-bottom:8px;">Available Metafield Keys:</strong><code style="display:block;word-break:break-all;background:#fff;padding:8px;border-radius:3px;">' + availableKeys + '</code>';
            this.insertAdjacentElement('afterbegin', hint);
          }
        }
      }

      // ═══════════════════════════════════════════════════════
      //  Combobox
      // ═══════════════════════════════════════════════════════

      _positionDropdown(slotIndex) {
        const wrap = this.querySelector('.dc-combobox[data-slot="' + slotIndex + '"]');
        const dd = this._detachedDropdowns && this._detachedDropdowns.get(slotIndex);
        if (!wrap || !dd) return;
        const rect = wrap.getBoundingClientRect();
        dd.style.position = 'fixed';
        dd.style.top = (rect.bottom + 4) + 'px';
        dd.style.left = rect.left + 'px';
        dd.style.width = rect.width + 'px';
        dd.style.zIndex = '9999';
      }

      _repositionOpenDropdowns() {
        if (!this._detachedDropdowns) return;
        this._detachedDropdowns.forEach((dd, slotIndex) => {
          if (!dd.hidden) this._positionDropdown(slotIndex);
        });
      }

      _openDropdown(slotIndex) {
        const wrap = this.querySelector('.dc-combobox[data-slot="' + slotIndex + '"]');
        const dd = this._detachedDropdowns && this._detachedDropdowns.get(slotIndex);
        if (!wrap || !dd) return;
        const input = wrap.querySelector('.dc-combobox__input');
        this._renderDropdownItems(slotIndex, input ? input.value.trim() : '');
        dd.removeAttribute('hidden');
        wrap.setAttribute('aria-expanded', 'true');
        this._positionDropdown(slotIndex);
      }

      _closeDropdown(slotIndex) {
        const wrap = this.querySelector('.dc-combobox[data-slot="' + slotIndex + '"]');
        const dd = this._detachedDropdowns && this._detachedDropdowns.get(slotIndex);
        if (dd) dd.setAttribute('hidden', '');
        this._activeDropdownIndex.delete(slotIndex);
        if (wrap) {
          wrap.setAttribute('aria-expanded', 'false');
          var slotData = this.slots[slotIndex];
          var input = wrap.querySelector('.dc-combobox__input');
          if (input) {
            input.removeAttribute('aria-activedescendant');
            if (slotData) input.value = this._getDropdownDisplayName(slotData);
          }
        }
      }

      _closeAllDropdowns() {
        if (!this._detachedDropdowns) return;
        this._detachedDropdowns.forEach(function(_, slotIndex) {
          this._closeDropdown(slotIndex);
        }.bind(this));
      }

      _filterDropdown(query, slotIndex) {
        this._renderDropdownItems(slotIndex, query);
      }

      _getDropdownOptions(slotIndex) {
        var dd = this._detachedDropdowns && this._detachedDropdowns.get(slotIndex);
        if (!dd) return [];
        return Array.from(dd.querySelectorAll('.dc-combobox__item'));
      }

      _setActiveDropdownOption(slotIndex, optionIndex) {
        var wrap = this.querySelector('.dc-combobox[data-slot="' + slotIndex + '"]');
        var input = wrap && wrap.querySelector('.dc-combobox__input');
        var options = this._getDropdownOptions(slotIndex);

        if (!options.length) {
          this._activeDropdownIndex.delete(slotIndex);
          if (input) input.removeAttribute('aria-activedescendant');
          return;
        }

        var clampedIndex = Math.max(0, Math.min(optionIndex, options.length - 1));
        this._activeDropdownIndex.set(slotIndex, clampedIndex);

        options.forEach(function(optionEl, idx) {
          var isActive = idx === clampedIndex;
          optionEl.classList.toggle('is-active', isActive);
          optionEl.setAttribute('aria-selected', isActive ? 'true' : 'false');
          if (isActive) {
            optionEl.scrollIntoView({ block: 'nearest' });
            if (input) input.setAttribute('aria-activedescendant', optionEl.id);
          }
        });
      }

      _moveActiveDropdownOption(slotIndex, delta) {
        var options = this._getDropdownOptions(slotIndex);
        if (!options.length) return;

        var current = this._activeDropdownIndex.has(slotIndex)
          ? this._activeDropdownIndex.get(slotIndex)
          : -1;

        var next = current + delta;
        if (next < 0) next = options.length - 1;
        if (next >= options.length) next = 0;
        this._setActiveDropdownOption(slotIndex, next);
      }

      _chooseActiveDropdownOption(slotIndex) {
        var options = this._getDropdownOptions(slotIndex);
        if (!options.length) return;

        var activeIndex = this._activeDropdownIndex.has(slotIndex)
          ? this._activeDropdownIndex.get(slotIndex)
          : 0;
        var optionEl = options[activeIndex] || options[0];
        if (!optionEl) return;

        var handle = optionEl.dataset.handle;
        if (!handle) return;
        this.selectProduct(handle, slotIndex);
        this._closeDropdown(slotIndex);
      }

      _filteredProducts(query) {
        if (!query) return this.allProducts;
        var q = query.toLowerCase();
        return this.allProducts.filter(function(p) {
          var title = (p.title || '').toLowerCase();
          var model = (p.modelName || '').toLowerCase();
          return title.includes(q) || model.includes(q);
        });
      }

      _renderDropdownItems(slotIndex, query) {
        var dd = this._detachedDropdowns && this._detachedDropdowns.get(slotIndex);
        if (!dd) return;
        dd.innerHTML = '';

        if (!this._productsLoaded) {
          dd.innerHTML = '<li class="dc-combobox__status">Loading\u2026</li>';
          return;
        }

        var self = this;
        var products = this._filteredProducts(query).filter(function(p) {
          return !self.slots.some(function(s) { return s && s.handle === p.handle; });
        });

        if (this._showModelNameDropdown) {
          this._ensureDropdownMetafields(products);
        }
        if (this._showShortDescDropdown) {
          this._ensureDropdownMetafields(products);
        }

        if (!products.length) {
          dd.innerHTML = '<li class="dc-combobox__status">No products found</li>';
          return;
        }

        var grouped = this._groupProductsByTag(products);
        var frag = document.createDocumentFragment();
        var optionIndex = 0;

        grouped.forEach(function(group, groupIdx) {
          if (group.label) {
            var header = document.createElement('li');
            header.className = 'dc-combobox__group-label';
            header.setAttribute('role', 'presentation');
            header.textContent = self._formatTagGroupLabel(group.label);
            frag.appendChild(header);
          }

          group.items.forEach(function(p) {
            var li = document.createElement('li');
            li.className = 'dc-combobox__item';
            li.dataset.handle = p.handle;
            li.id = 'dc-combobox-option-' + slotIndex + '-' + optionIndex;
            li.setAttribute('role', 'option');
            li.setAttribute('aria-selected', 'false');

            var imgHtml = '';
            if (p.image) {
              var src = p.image.includes('?')
                ? p.image.replace(/(\?.*)?$/, '?width=80')
                : p.image + '?width=80';
              imgHtml = '<img src="' + src + '" alt="' + self._esc(p.title) + '" loading="lazy" width="40" height="40">';
            } else {
              imgHtml = '<span class="dc-combobox__no-img"></span>';
            }

            var displayName = self._getDropdownDisplayName(p);
            var subtitle = self._getDropdownSubtitle(p);
            var textHtml = '<span class="dc-combobox__item-text"><span class="dc-combobox__item-title">' + self._esc(displayName) + '</span>';
            if (subtitle) {
              textHtml += '<span class="dc-combobox__item-subtitle">' + self._esc(subtitle) + '</span>';
            }
            textHtml += '</span>';

            li.innerHTML = imgHtml + textHtml;

            var selected = false;
            var chooseProduct = function(e) {
              if (selected) return;
              selected = true;
              if (e) e.preventDefault();
              self.selectProduct(p.handle, slotIndex);
              self._closeDropdown(slotIndex);
            };

            if (window.PointerEvent) {
              li.addEventListener('pointerdown', chooseProduct);
            } else {
              li.addEventListener('mousedown', chooseProduct);
              li.addEventListener('touchstart', chooseProduct, { passive: false });
            }
            li.addEventListener('click', chooseProduct);
            frag.appendChild(li);
            optionIndex += 1;
          });

          if (groupIdx < grouped.length - 1) {
            var divider = document.createElement('li');
            divider.className = 'dc-combobox__group-divider';
            divider.setAttribute('role', 'presentation');
            frag.appendChild(divider);
          }
        });

        dd.appendChild(frag);

        if (optionIndex > 0) {
          var prevActive = this._activeDropdownIndex.has(slotIndex) ? this._activeDropdownIndex.get(slotIndex) : 0;
          this._setActiveDropdownOption(slotIndex, prevActive);
        } else {
          this._activeDropdownIndex.delete(slotIndex);
        }
      }

      _handleClickOutside(e) {
        if (e.target.closest('.dc-combobox')) return;
        var clickedDetached = Array.from((this._detachedDropdowns || new Map()).values())
          .some(function(dd) { return dd.contains(e.target); });
        if (!clickedDetached) this._closeAllDropdowns();
      }

      // ═══════════════════════════════════════════════════════
      //  Product loading
      // ═══════════════════════════════════════════════════════

      async _loadAllProducts() {
        try {
          var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
            ? window.Shopify.routes.root
            : ((window.shopUrl || '').replace(/\/$/, '') + '/');
          if (!root.endsWith('/')) root += '/';

          var currentCollectionHandle = (this.dataset.currentCollectionHandle || '').trim();
          var configuredCollectionHandle = (this.dataset.collectionHandle || '').trim();

          var baseProductsPromise = configuredCollectionHandle
            ? this._fetchProducts(root + 'collections/' + configuredCollectionHandle + '/products.json?limit=250')
            : this._fetchProducts(root + 'products.json?limit=250');

          var currentCollectionPromise = currentCollectionHandle
            ? this._fetchProducts(root + 'collections/' + currentCollectionHandle + '/products.json?limit=250')
            : Promise.resolve([]);

          var results = await Promise.all([baseProductsPromise, currentCollectionPromise]);
          this._processProducts(results[0] || [], results[1] || []);
        } catch (err) {
          console.error('[dynamic-comparison] product load error:', err);
          this._productsLoaded = true;
        }
      }

      async _fetchProducts(url) {
        var response = await fetch(url);
        if (!response.ok) {
          throw new Error('Fetch failed: ' + response.status);
        }
        var data = await response.json();
        return data.products || [];
      }

      _mapProducts(products) {
        return products.map(function(p) {
          var tags = [];
          if (Array.isArray(p.tags)) {
            tags = p.tags.map(function(t) { return String(t).trim(); }).filter(Boolean);
          } else if (typeof p.tags === 'string') {
            tags = p.tags.split(',').map(function(t) { return t.trim(); }).filter(Boolean);
          }

          var modelName = '';
          if (p.metafields && p.metafields.model_name) {
            modelName = String(p.metafields.model_name);
          }

          var shortDesc = '';
          if (p.metafields && p.metafields.short_desc) {
            shortDesc = String(p.metafields.short_desc);
          }

          return {
            title: p.title,
            handle: p.handle,
            image: (p.images && p.images[0]) ? p.images[0].src : null,
            tags: tags,
            modelName: modelName,
            shortDesc: shortDesc
          };
        });
      }

      _processProducts(baseProducts, currentCollectionProducts) {
        this._baseProducts = this._mapProducts(baseProducts);
        this._currentCollectionProducts = this._mapProducts(currentCollectionProducts);
        this._rebuildProductList();
        this._productsLoaded = true;

        // Refresh open dropdowns
        var self = this;
        if (this._detachedDropdowns) {
          this._detachedDropdowns.forEach(function(dd, slotIndex) {
            if (!dd.hidden) {
              var wrap = self.querySelector('.dc-combobox[data-slot="' + slotIndex + '"]');
              var input = wrap && wrap.querySelector('.dc-combobox__input');
              self._renderDropdownItems(slotIndex, input ? input.value.trim() : '');
            }
          });
        }
      }

      _rebuildProductList() {
        var seen = new Set();
        var currentFirst = [];
        this._currentCollectionProducts.forEach(function(p) {
          if (!seen.has(p.handle)) {
            seen.add(p.handle);
            currentFirst.push(p);
          }
        });

        var others = [];
        this._baseProducts.forEach(function(p) {
          if (!seen.has(p.handle)) {
            seen.add(p.handle);
            others.push(p);
          }
        });

        if (this._useCurrentCollection && currentFirst.length > 0) {
          this.allProducts = currentFirst;
        } else {
          this.allProducts = currentFirst.concat(others);
        }
      }

      _buildInitialFillProducts(currentHandle, needed) {
        if (needed <= 0) return [];

        var currentSet = new Set();
        var currentPool = this._currentCollectionProducts.filter(function(p) {
          var ok = p.handle !== currentHandle;
          if (ok) currentSet.add(p.handle);
          return ok;
        });

        var out = currentPool.slice(0, needed);
        if (out.length >= needed) return out;

        if (this._useCurrentCollection) {
          return out;
        }

        var others = this._baseProducts.filter(function(p) {
          return p.handle !== currentHandle && !currentSet.has(p.handle);
        });
        var shuffled = this._shuffleArray(others);
        return out.concat(shuffled.slice(0, needed - out.length));
      }

      _shuffleArray(arr) {
        var copy = arr.slice();
        for (var i = copy.length - 1; i > 0; i--) {
          var j = Math.floor(Math.random() * (i + 1));
          var tmp = copy[i];
          copy[i] = copy[j];
          copy[j] = tmp;
        }
        return copy;
      }

      async _loadCurrentProduct(jsonStr) {
        try {
          var meta = JSON.parse(jsonStr);
          if (!meta.handle) return;

          var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
            ? window.Shopify.routes.root
            : ((window.shopUrl || '').replace(/\/$/, '') + '/');
          if (!root.endsWith('/')) root += '/';

          var response = await fetch(root + 'products/' + meta.handle + '?view=data');
          if (!response.ok) return;
          var product = JSON.parse(await response.text());

          this.slots[0] = this._parseProduct(product, meta);
        } catch (e) {
          console.warn('[dynamic-comparison] Failed to load current product:', e);
        }
      }

      _loadProductIntoSlot(handle, slotIndex) {
        var self = this;
        return new Promise(function(resolve) {
          if (self.slots[slotIndex] && self.slots[slotIndex].handle === handle) {
            resolve();
            return;
          }

          var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
            ? window.Shopify.routes.root
            : ((window.shopUrl || '').replace(/\/$/, '') + '/');
          if (!root.endsWith('/')) root += '/';

          fetch(root + 'products/' + handle + '?view=data')
            .then(function(r) { return r.ok ? r.text() : Promise.reject(); })
            .then(function(text) { return JSON.parse(text); })
            .then(function(product) {
              self.slots[slotIndex] = self._parseProduct(product);
              self.updateSlot(slotIndex);
              self._updateSpecValues();
              resolve();
            })
            .catch(function() { resolve(); });
        });
      }

      selectProduct(handle, slotIndex) {
        var self = this;
        if (this.slots.some(function(s) { return s && s.handle === handle; })) return;
        if (slotIndex >= this.MAX_SLOTS) return;

        var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
          ? window.Shopify.routes.root
          : ((window.shopUrl || '').replace(/\/$/, '') + '/');
        if (!root.endsWith('/')) root += '/';

        fetch(root + 'products/' + handle + '?view=data')
          .then(function(r) {
            if (!r.ok) throw new Error(r.status);
            return r.text();
          })
          .then(function(text) { return JSON.parse(text); })
          .then(function(product) {
            self.slots[slotIndex] = self._parseProduct(product);
            self.updateSlot(slotIndex);
            self._updateSpecValues();
          })
          .catch(function(err) {
            console.error('[dynamic-comparison] product load error:', err);
          });
      }

      _parseProduct(product, meta) {
        var optionMap = {};
        var colorValues = [];

        (product.options || []).forEach(function(opt) {
          if (opt.name !== 'Title') {
            optionMap[opt.name.toLowerCase().trim()] = opt.values.join(', ');
            var nameLower = opt.name.toLowerCase();
            if (nameLower === 'color' || nameLower === 'colour') {
              colorValues = colorValues.concat(opt.values);
            }
          }
        });

        if (product.metafields) {
          Object.keys(product.metafields).forEach(function(key) {
            var val = product.metafields[key];
            if (val !== null && val !== undefined) {
              optionMap[key.toLowerCase().trim()] = Array.isArray(val) ? val.join(', ') : String(val);
            }
          });
        }

        // Resolve images: featured, second, metafield
        var featuredImage = (meta && meta.image) || (product.featured_image && product.featured_image.src) || null;
        var secondImage = null;
        if (product.images && product.images.length > 1) {
          var secondSrc = typeof product.images[1] === 'string' ? product.images[1] : (product.images[1] && product.images[1].src);
          if (secondSrc) secondImage = secondSrc;
        }

        // Check for metafield image
        var metafieldImage = null;
        if (this._imageMetafield && product.metafields && product.metafields[this._imageMetafield]) {
          var mfVal = product.metafields[this._imageMetafield];
          if (typeof mfVal === 'string' && (mfVal.includes('cdn.shopify') || mfVal.startsWith('http') || mfVal.startsWith('//'))) {
            metafieldImage = mfVal;
          }
        }

        return {
          handle: (meta && meta.handle) || product.handle,
          title: (meta && meta.title) || product.title,
          url: product.url || (meta && meta.url) || '',
          image: metafieldImage || featuredImage,
          secondImage: secondImage,
          price: product.price,
          options: Object.keys(optionMap),
          optionMap: optionMap,
          colorValues: colorValues,
          modelName: optionMap.model_name || ''
        };
      }

      // ═══════════════════════════════════════════════════════
      //  Rendering
      // ═══════════════════════════════════════════════════════

      _renderSpecStructure() {
        if (!this.configRows.length) return;

        // Build spec items in each product column
        var self = this;
        for (var i = 0; i < this.MAX_SLOTS; i++) {
          var specsEl = this.querySelector('.dc-col__specs[data-slot="' + i + '"]');
          if (!specsEl) continue;
          specsEl.innerHTML = '';

          this.configRows.forEach(function(row) {
            var item = document.createElement('div');
            item.className = 'dc-spec-item';
            item.dataset.rowKey = row.key.toLowerCase();
            item.dataset.mode = row.mode;
            if (row.checkImg) item.dataset.checkImg = row.checkImg;
            if (row.crossImg) item.dataset.crossImg = row.crossImg;

            var html = '<div class="dc-spec__value">\u2014</div>';
            html += '<div class="dc-spec__label">' + self._esc(row.label) + '</div>';
            item.innerHTML = html;
            specsEl.appendChild(item);
          });
        }
      }

      updateSlot(slotIndex) {
        var slotData = this.slots[slotIndex];
        var productEl = this.querySelector('.dc-col__product[data-slot="' + slotIndex + '"]');
        var combobox = this.querySelector('.dc-combobox[data-slot="' + slotIndex + '"]');

        if (!slotData) {
          if (productEl) productEl.innerHTML = '<div class="dc-placeholder"><span>Add product</span></div>';
          if (combobox) {
            var input = combobox.querySelector('.dc-combobox__input');
            if (input) input.value = '';
          }
          this._updateSpecValues();
          return;
        }

        // Update combobox title
        if (combobox) {
          var input = combobox.querySelector('.dc-combobox__input');
          if (input) input.value = this._getDropdownDisplayName(slotData);
        }

        // Build product card
        var html = '<div class="dc-product-card">';

        // Clickable image with hover
        if (this._showImage && slotData.image) {
          var imgSrc = slotData.image.includes('?')
            ? slotData.image.replace(/(\?.*)?$/, '?width=450')
            : slotData.image + '?width=450';
          html += '<a href="' + slotData.url + '" class="dc-product-card__image-link">';
          html += '<img src="' + imgSrc + '" alt="' + this._esc(slotData.title) + '" loading="lazy" width="225" height="225" class="dc-product-card__img dc-product-card__img--primary">';
          if (slotData.secondImage) {
            var hoverSrc = slotData.secondImage.includes('?')
              ? slotData.secondImage.replace(/(\?.*)?$/, '?width=450')
              : slotData.secondImage + '?width=450';
            html += '<img src="' + hoverSrc + '" alt="' + this._esc(slotData.title) + '" loading="lazy" width="225" height="225" class="dc-product-card__img dc-product-card__img--hover">';
          }
          html += '</a>';
        }

        // Info section (pushed to bottom via flex)
        html += '<div class="dc-product-card__info">';

        // Color swatches
        if (this._showColorSwatches && slotData.colorValues && slotData.colorValues.length > 0) {
          html += '<div class="dc-product-card__swatches">';
          var self = this;
          slotData.colorValues.forEach(function(color) {
            var cssColor = self._getColorCSS(color);
            html += '<span class="dc-swatch" style="background-color: ' + cssColor + ';" title="' + self._esc(color) + '"></span>';
          });
          html += '</div>';
        }

        // Title
        html += '<p class="dc-product-card__title">' + this._esc(slotData.title) + '</p>';

        // Price
        if (slotData.price != null) {
          html += '<p class="dc-product-card__price">' + this._esc(this._pricePrefix) + ' ' + this._formatMoney(slotData.price) + '</p>';
        }

        // Learn more
        if (this._showLearnMore) {
          html += '<a href="' + slotData.url + '" class="dc-product-card__learn-more">' + this._esc(this._learnMoreText) + '</a>';
        }

        html += '</div>'; // close __info
        html += '</div>'; // close product-card
        if (productEl) productEl.innerHTML = html;

        this._updateSpecValues();
      }

      _updateSpecValues() {
        for (var i = 0; i < this.MAX_SLOTS; i++) {
          var slotData = this.slots[i];
          var specsEl = this.querySelector('.dc-col__specs[data-slot="' + i + '"]');
          if (!specsEl) continue;

          var items = specsEl.querySelectorAll('.dc-spec-item');
          for (var j = 0; j < items.length; j++) {
            var item = items[j];
            var key = item.dataset.rowKey;
            var mode = item.dataset.mode || 'text';
            var valueEl = item.querySelector('.dc-spec__value');
            if (!valueEl) continue;

            if (!slotData) {
              valueEl.innerHTML = '';
              continue;
            }

            var rawValue = slotData.optionMap[key] || '';

            if (mode === 'boolean') {
              var isTruthy = this._isTruthy(rawValue);
              var checkImg = item.dataset.checkImg;
              var crossImg = item.dataset.crossImg;

              if (isTruthy) {
                valueEl.innerHTML = checkImg
                  ? '<img src="' + checkImg + '" alt="\u2713" class="dc-bool-img" width="20" height="20">'
                  : '<span class="dc-bool dc-bool--check">\u2713</span>';
              } else {
                valueEl.innerHTML = crossImg
                  ? '<img src="' + crossImg + '" alt="\u2717" class="dc-bool-img" width="20" height="20">'
                  : '<span class="dc-bool dc-bool--cross">\u2717</span>';
              }
            } else {
              valueEl.textContent = this._formatSpecValue(rawValue);
            }
          }
        }
      }

      _formatSpecValue(rawValue) {
        var text = rawValue == null ? '' : String(rawValue);
        if (!text) return '\u2014';
        if (this._shouldSkipTransformForValue(text)) return text;

        switch (this._tableTextTransform) {
          case 'uppercase':
            return text.toUpperCase();
          case 'lowercase':
            return text.toLowerCase();
          case 'capitalize':
            return text.replace(/\b([A-Za-z])([A-Za-z']*)/g, function(_, first, rest) {
              return first.toUpperCase() + rest.toLowerCase();
            });
          default:
            return text;
        }
      }

      _shouldSkipTransformForValue(text) {
        return /\d/.test(text);
      }

      clearSlot(slotIndex) {
        this.slots[slotIndex] = null;
        this.updateSlot(slotIndex);
      }

      // ═══════════════════════════════════════════════════════
      //  Utilities
      // ═══════════════════════════════════════════════════════

      _isTruthy(val) {
        if (!val || val === '\u2014') return false;
        var v = val.toString().toLowerCase().trim();
        return v !== '' && v !== 'false' && v !== 'no' && v !== '0' && v !== 'n/a' && v !== 'none';
      }

      _formatMoney(priceCents) {
        if (priceCents == null) return '';
        var price = priceCents / 100;
        try {
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          }).format(price);
        } catch (e) {
          return '$' + price.toLocaleString();
        }
      }

      _getColorCSS(colorName) {
        var map = {
          'black': '#000', 'white': '#fff', 'red': '#e53e3e', 'blue': '#3182ce',
          'green': '#38a169', 'yellow': '#ecc94b', 'pink': '#ed64a6', 'purple': '#805ad5',
          'orange': '#dd6b20', 'gray': '#a0aec0', 'grey': '#a0aec0', 'silver': '#cbd5e0',
          'gold': '#ffd700', 'navy': '#2a4365', 'brown': '#744210', 'beige': '#f5f5dc',
          'cream': '#fefcbf', 'teal': '#319795', 'coral': '#fc8181', 'ivory': '#fffff0',
          'maroon': '#9b2c2c', 'olive': '#6b7c3b', 'charcoal': '#4a5568', 'tan': '#d2b48c',
          'burgundy': '#742a2a', 'mint': '#9ae6b4', 'lavender': '#e9d8fd', 'rose': '#feb2b2',
          'sky': '#90cdf4', 'slate': '#718096'
        };
        var key = colorName.toLowerCase().trim();
        return map[key] || colorName;
      }

      _esc(str) {
        if (!str) return '';
        return str
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      }

      _parseTagGroups(raw) {
        if (!raw) return [];
        var normalizedRaw = raw
          .replace(/；/g, ';')
          .replace(/，/g, ',')
          .replace(/：/g, ':')
          .replace(/\u3000/g, ' ');

        var chunks = normalizedRaw
          .split(/\r?\n|;/)
          .map(function(line) { return line.trim(); })
          .filter(Boolean);

        var groups = [];

        chunks.forEach(function(line) {
          var parts = line.includes('|') ? line.split('|') : line.split(':');
          var label = (parts[0] || '').trim();
          var tagsRaw = (parts.slice(1).join('|') || '').trim();
          if (!label) return;

          var tags = tagsRaw
            ? tagsRaw.split(',').map(function(tag) { return tag.trim().toLowerCase(); }).filter(Boolean)
            : [label.toLowerCase()];

          groups.push({ label: label, tags: tags });
        });

        return groups;
      }

      _formatTagGroupLabel(label) {
        var text = (label || '').trim();
        if (!text) return '';
        if (/\bseries$/i.test(text)) return text;
        return text + ' Series';
      }

      _groupProductsByTag(products) {
        if (!this._tagGroups.length) {
          return [{ label: '', items: products }];
        }

        var groups = this._tagGroups.map(function(cfg) {
          return { label: cfg.label, tags: cfg.tags, items: [] };
        });
        var others = [];

        products.forEach(function(product) {
          var productTags = (product.tags || []).map(function(tag) { return tag.toLowerCase(); });
          var productTagsNormalized = productTags.map(function(tag) { return tag.replace(/[\s_-]+/g, ' ').trim(); });
          var matched = false;

          for (var i = 0; i < groups.length; i++) {
            var group = groups[i];
            var hasTag = group.tags.some(function(tag) {
              var key = tag.toLowerCase().replace(/[\s_-]+/g, ' ').trim();
              return productTagsNormalized.some(function(pt) {
                return pt === key || pt.indexOf(key) !== -1 || key.indexOf(pt) !== -1;
              });
            });
            if (hasTag) {
              group.items.push(product);
              matched = true;
              break;
            }
          }

          if (!matched) others.push(product);
        });

        var result = groups.filter(function(g) { return g.items.length > 0; }).map(function(g) {
          return { label: g.label, items: g.items };
        });

        if (others.length) {
          result.push({ label: 'Other Products', items: others });
        }

        return result;
      }

      _getDropdownDisplayName(product) {
        if (this._showModelNameDropdown && product && product.modelName) {
          return product.modelName;
        }
        return (product && product.title) || '';
      }

      _getDropdownSubtitle(product) {
        if (this._showShortDescDropdown && product && product.shortDesc) {
          return this._stripHtml(product.shortDesc);
        }
        if (this._showProductTitleSubtitle && product) {
          return product.title || '';
        }
        return '';
      }

      _ensureDropdownMetafields(products) {
        var self = this;
        products.forEach(function(product) {
          if (!product || !product.handle || self._modelNameRequests.has(product.handle)) return;

          var needsModel = self._showModelNameDropdown && !product.modelName;
          var needsShortDesc = self._showShortDescDropdown && !product.shortDesc;
          if (!needsModel && !needsShortDesc) return;

          self._modelNameRequests.add(product.handle);

          var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
            ? window.Shopify.routes.root
            : ((window.shopUrl || '').replace(/\/$/, '') + '/');
          if (!root.endsWith('/')) root += '/';

          fetch(root + 'products/' + product.handle + '?view=data')
            .then(function(r) { return r.ok ? r.text() : Promise.reject(); })
            .then(function(text) { return JSON.parse(text); })
            .then(function(data) {
              if (data && data.metafields && data.metafields.model_name) {
                product.modelName = String(data.metafields.model_name);
              }
              if (data && data.metafields && data.metafields.short_desc) {
                product.shortDesc = String(data.metafields.short_desc);
              }
            })
            .catch(function() {})
            .finally(function() {
              self._modelNameRequests.delete(product.handle);
              if (!self._detachedDropdowns) return;
              self._detachedDropdowns.forEach(function(dd, slotIndex) {
                if (!dd.hidden) {
                  var wrap = self.querySelector('.dc-combobox[data-slot="' + slotIndex + '"]');
                  var input = wrap && wrap.querySelector('.dc-combobox__input');
                  self._renderDropdownItems(slotIndex, input ? input.value.trim() : '');
                }
              });
            });
        });
      }

      _stripHtml(str) {
        if (!str) return '';
        return str.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
      }

      // ═══════════════════════════════════════════════════════
      //  Mouse-drag horizontal scroll
      // ═══════════════════════════════════════════════════════

      _initDragScroll() {
        var el = this.querySelector('.dc-columns');
        if (!el) return;
        var isDown = false;
        var startX = 0;
        var scrollLeft = 0;

        el.addEventListener('mousedown', function(e) {
          // Don't interfere with inputs/links
          if (e.target.closest('input, a, button, select')) return;
          isDown = true;
          startX = e.pageX - el.offsetLeft;
          scrollLeft = el.scrollLeft;
          el.style.scrollBehavior = 'auto';
        });

        el.addEventListener('mouseleave', function() { isDown = false; });
        el.addEventListener('mouseup', function() {
          isDown = false;
          el.style.scrollBehavior = '';
        });

        el.addEventListener('mousemove', function(e) {
          if (!isDown) return;
          e.preventDefault();
          var x = e.pageX - el.offsetLeft;
          el.scrollLeft = scrollLeft - (x - startX);
        });
      }

    }
  );
}
